import React, { useEffect, useMemo, useState } from "react";
import type { User } from "firebase/auth";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Pagination,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  subscribeToVesPayments,
  approveVesPayment,
  rejectVesPayment,
  VesPaymentRequestRecord,
} from "../../api/vesPayments";

const PageIntro = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <header className="content__header mb-4">
    <div>
      <h1 className="page-header__title">{title}</h1>
      <p className="page-header__lead">{description}</p>
    </div>
  </header>
);

const PAGE_SIZE = 15;

type StatusFilter = "pending" | "approved" | "rejected" | "all";

const vesItemLabel = (r: VesPaymentRequestRecord): string => {
  if (r.product === "intelligence_pack") {
    const qty = r.packQuantity && r.packQuantity > 0 ? r.packQuantity : 1;
    const name = r.packName || "paquete Intelligence";
    const credits = r.packCredits ? ` · ${r.packCredits} créditos` : "";
    return `${qty}× ${name}${credits}`;
  }
  if (r.product === "intelligence") {
    const cap = (r.capacityId || "").toUpperCase();
    return cap ? `Intelligence ${cap}` : "Intelligence";
  }
  return r.plan || "Plan MayorX";
};

const vesProductKind = (r: VesPaymentRequestRecord): string => {
  if (r.product === "intelligence_pack") return "Intel pack";
  if (r.product === "intelligence") return "Intelligence";
  return "Plan MayorX";
};

const VesPaymentsPage: React.FC<{ user: User }> = ({ user }) => {
  const [records, setRecords] = useState<VesPaymentRequestRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [page, setPage] = useState(1);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState<{
    record: VesPaymentRequestRecord;
  } | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [detailModal, setDetailModal] = useState<VesPaymentRequestRecord | null>(null);

  useEffect(() => {
    setLoading(true);
    setPage(1);
    const unsubscribe = subscribeToVesPayments(
      (recs) => {
        setRecords(recs);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
      statusFilter === "all" ? undefined : statusFilter
    );
    return () => unsubscribe();
  }, [statusFilter]);

  const handleApprove = async (record: VesPaymentRequestRecord) => {
    setApprovingId(record.id);
    setError(null);
    try {
      await approveVesPayment(
        record.id,
        record.organizationId,
        user,
        record.storagePath
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al aprobar");
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    const { record } = rejectModal;
    setRejectingId(record.id);
    setError(null);
    try {
      await rejectVesPayment(
        record.id,
        record.organizationId,
        user,
        rejectReason.trim() || undefined
      );
      setRejectModal(null);
      setRejectReason("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al rechazar");
    } finally {
      setRejectingId(null);
    }
  };

  const formatDate = (d: Date | null) =>
    d ? d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

  const formatVesAmount = (amount: number): string => {
    // Format with commas as thousands separator and dots for decimals
    return amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  };

  const getExpectedVes = (r: VesPaymentRequestRecord) =>
    Math.round(r.amountUSD * r.exchangeRate);
  const isAmountMismatch = (r: VesPaymentRequestRecord) => {
    const expected = getExpectedVes(r);
    return Math.abs(r.amountVES - expected) > 1;
  };

  const totalPages = Math.max(1, Math.ceil(records.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRecords = useMemo(
    () => records.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [records, safePage]
  );
  const isIntel = (r: VesPaymentRequestRecord) =>
    r.product === "intelligence" || r.product === "intelligence_pack";

  return (
    <>
      <PageIntro
        title="Pagos en Bolívares (VES)"
        description="Revisa cada comprobante. Plan MayorX activa la suscripción; Intelligence (capacidad o paquetes de créditos) no bloquea el plan base. Al rechazar un plan MayorX el usuario queda bloqueado; al rechazar Intelligence solo se cancela esa compra."
      />
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Card className="shadow-sm">
        <Card.Header className="border-0 d-flex flex-wrap align-items-center justify-content-between gap-2">
          <Card.Title className="mb-0">Solicitudes</Card.Title>
          <Form.Select
            size="sm"
            style={{ width: "auto" }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            aria-label="Filtrar por estado"
          >
            <option value="pending">Pendientes</option>
            <option value="approved">Aprobadas</option>
            <option value="rejected">Rechazadas</option>
            <option value="all">Todas</option>
          </Form.Select>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner animation="border" size="sm" />
              <span>Cargando...</span>
            </div>
          ) : records.length === 0 ? (
            <div className="text-muted small">
              {statusFilter === "pending"
                ? "No hay solicitudes pendientes."
                : "No hay solicitudes en este filtro."}
            </div>
          ) : (
            <Table responsive size="sm" className="mb-0 align-middle text-nowrap">
              <thead>
                <tr>
                  <th>Organización</th>
                  <th>Qué se paga</th>
                  <th>Monto esperado</th>
                  <th>Monto reportado</th>
                  <th>Tipo cambio</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                  <th>Verificación</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pageRecords.map((r) => {
                  const expectedVes = getExpectedVes(r);
                  const mismatch = isAmountMismatch(r);
                  return (
                    <tr key={r.id} className={mismatch ? "table-warning" : undefined}>
                      <td>
                        <div className="fw-semibold">{r.organizationName || r.organizationId}</div>
                        <div className="text-muted small">{r.organizationId}</div>
                      </td>
                      <td>
                        <Badge bg={isIntel(r) ? "info" : "secondary"}>
                          {vesProductKind(r)}
                        </Badge>
                        <div className="fw-semibold small mt-1">{vesItemLabel(r)}</div>
                        <div className="text-muted small">${r.amountUSD} USD</div>
                      </td>
                      <td>
                        <strong>{formatVesAmount(expectedVes)} Bs</strong>
                        <div className="text-muted small">(${r.amountUSD} × {r.exchangeRate.toLocaleString()})</div>
                      </td>
                      <td>
                        <span className={mismatch ? "text-danger fw-semibold" : ""}>
                          {formatVesAmount(r.amountVES)} Bs
                        </span>
                        {mismatch && (
                          <Badge bg="warning" className="ms-1">¿Monto incorrecto?</Badge>
                        )}
                      </td>
                      <td>{r.exchangeRate.toLocaleString()}</td>
                      <td>
                        <Badge
                          bg={
                            r.status === "pending"
                              ? "warning"
                              : r.status === "approved"
                                ? "success"
                                : "danger"
                          }
                        >
                          {r.status}
                        </Badge>
                      </td>
                      <td className="text-muted small">{formatDate(r.createdAt)}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setDetailModal(r)}
                        >
                          Ver captura y verificar
                        </Button>
                      </td>
                      <td>
                        {r.status === "pending" ? (
                          <div className="d-flex gap-2">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleApprove(r)}
                              disabled={approvingId === r.id}
                            >
                              {approvingId === r.id ? "Aprobando..." : "Aprobar"}
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => setRejectModal({ record: r })}
                              disabled={rejectingId === r.id}
                            >
                              {rejectingId === r.id ? "Rechazando..." : "Rechazar"}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          {records.length > 0 ? (
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
              <div className="text-muted small">
                {records.length} solicitud{records.length === 1 ? "" : "es"}
                {records.length > PAGE_SIZE
                  ? ` · página ${safePage} de ${totalPages}`
                  : ""}
              </div>
              {totalPages > 1 ? (
                <Pagination className="mb-0" size="sm">
                  <Pagination.Prev
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  />
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (n) =>
                        n === 1 ||
                        n === totalPages ||
                        Math.abs(n - safePage) <= 1
                    )
                    .reduce<number[]>((acc, n) => {
                      if (acc.length && n - acc[acc.length - 1] > 1) {
                        acc.push(-n);
                      }
                      acc.push(n);
                      return acc;
                    }, [])
                    .map((n) =>
                      n < 0 ? (
                        <Pagination.Ellipsis key={`e${n}`} disabled />
                      ) : (
                        <Pagination.Item
                          key={n}
                          active={n === safePage}
                          onClick={() => setPage(n)}
                        >
                          {n}
                        </Pagination.Item>
                      )
                    )}
                  <Pagination.Next
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  />
                </Pagination>
              ) : null}
            </div>
          ) : null}
          <div className="text-muted small mt-3">
            <strong>Verificación:</strong> Confirma que la captura muestre el monto correcto según el precio asignado a la organización (amountUSD × tasa). Si el monto reportado no coincide o la transacción es inválida, <strong>rechaza</strong> y el usuario será bloqueado.
          </div>
        </Card.Body>
      </Card>

      <Modal
        show={!!detailModal}
        onHide={() => setDetailModal(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Verificar transacción VES</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailModal && (
            <>
              <div className="row mb-4">
                <div className="col-md-6">
                  <h6 className="text-muted">Organización</h6>
                  <p className="mb-1 fw-semibold">{detailModal.organizationName || detailModal.organizationId}</p>
                  <p className="text-muted small mb-0">{detailModal.organizationId}</p>
                </div>
                <div className="col-md-6">
                  <h6 className="text-muted">Qué se paga</h6>
                  <p className="mb-1">
                    <strong>{vesProductKind(detailModal)}</strong>
                    {" · "}
                    {vesItemLabel(detailModal)}
                  </p>
                  <p className="mb-1">${detailModal.amountUSD} USD</p>
                  <p className="mb-1">
                    Tipo de cambio: {detailModal.exchangeRate.toLocaleString()}<br />
                    Monto esperado: <strong>{formatVesAmount(getExpectedVes(detailModal))} Bs</strong><br />
                    Monto reportado: <span className={isAmountMismatch(detailModal) ? "text-danger fw-semibold" : ""}>{formatVesAmount(detailModal.amountVES)} Bs</span>
                  </p>
                  {isAmountMismatch(detailModal) && (
                    <Alert variant="warning" className="py-2 mb-0 small">
                      El monto reportado no coincide con el esperado. Considera rechazar si la captura no muestra el valor correcto.
                    </Alert>
                  )}
                </div>
              </div>
              <h6 className="text-muted mb-2">Captura de la transacción</h6>
              <p className="small text-muted mb-2">
                Verifica que la captura corresponda al monto de <strong>{formatVesAmount(detailModal.amountVES)} Bs</strong> y que la transacción sea válida.
              </p>
              <div className="mb-3">
                <img
                  src={detailModal.captureUrl}
                  alt="Captura de transacción"
                  className="rounded border"
                  style={{ maxWidth: "100%", maxHeight: 400, objectFit: "contain" }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
                <a href={detailModal.captureUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm mt-2">
                  Abrir captura / PDF en nueva pestaña
                </a>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {detailModal && (
            <>
              <Button variant="outline-secondary" onClick={() => setDetailModal(null)}>
                Cerrar
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setRejectModal({ record: detailModal });
                  setDetailModal(null);
                }}
              >
                Rechazar
              </Button>
              <Button
                variant="success"
                onClick={() => {
                  handleApprove(detailModal);
                  setDetailModal(null);
                }}
                disabled={approvingId === detailModal.id}
              >
                {approvingId === detailModal.id ? "Aprobando..." : "Aprobar transacción"}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!rejectModal}
        onHide={() => {
          setRejectModal(null);
          setRejectReason("");
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Rechazar pago VES</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rejectModal && (
            <p className="mb-3">
              {isIntel(rejectModal.record)
                ? `Al rechazar, no se acreditará ${vesItemLabel(rejectModal.record)} para ${rejectModal.record.organizationName || "esta organización"}. El plan MayorX no se bloquea.`
                : `Al rechazar, el usuario ${rejectModal.record.organizationName} será bloqueado. Opcionalmente indica el motivo (para soporte).`}
            </p>
          )}
          <Form.Group>
            <Form.Label>Motivo del rechazo (opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ej: Monto incorrecto, captura ilegible..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setRejectModal(null);
              setRejectReason("");
            }}
          >
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleReject} disabled={!rejectModal}>
            {!rejectModal
              ? "Rechazar"
              : rejectingId === rejectModal.record.id
                ? "Rechazando..."
                : isIntel(rejectModal.record)
                  ? "Rechazar compra"
                  : "Rechazar y bloquear usuario"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VesPaymentsPage;
